import { TFunction } from "i18next";
import {
	AccentColor,
	PrimaryColor,
	SecondaryColor,
	SemanticColor,
} from "node-library/dist/colors";
import {
	AdminViewOrderStatus,
	OrderItemType,
	OrderStatus,
	OrderType,
	PaymentStatus,
	TurnaroundTime,
	WarrantyUnit,
} from "node-library/dist/constants";
import { OrderTableSortColumn } from "node-library/dist/paginations";
import {
	FrontendOrder,
	FrontendOrderItem,
	FrontendProduct,
} from "node-library/dist/portal";
import { Permission } from "../permissionProvider/PermissionProvider";

export function getStatusString(
	status: OrderStatus,
	translation: TFunction<"translation", undefined>
): string {
	switch (status) {
		case OrderStatus.INCOMPLETE:
			return translation("Incomplete");
		case OrderStatus.PENDING:
			return translation("Pending");
		case OrderStatus.IN_PROGRESS:
			return translation("InProgress");
		// case OrderStatus.READY_TO_SHIP:
		// 	return translation("ReadyToShip");
		case OrderStatus.SHIPPED:
			return translation("Complete");
		case OrderStatus.DELIVERED:
			return translation("Delivered");
	}
}

export function getStatusDescriptionText(
	status: OrderStatus,
	translation: TFunction<"translation", undefined>
): string {
	switch (status) {
		case OrderStatus.INCOMPLETE:
			return translation("IncompleteOrderDescription");
		case OrderStatus.PENDING:
			return translation("PendingOrderDescription");
		case OrderStatus.IN_PROGRESS:
			return translation("InProgressOrderDescription");
		// case OrderStatus.READY_TO_SHIP:
		// 	return translation("ReadyToShipOrderDescription");
		case OrderStatus.SHIPPED:
			return translation("ShipOrderDescription");
		case OrderStatus.DELIVERED:
			return translation("ReceiveOrderDescription");
	}
}
export function getAdminStatusStringByAdminStatus(
	status: AdminViewOrderStatus,
	translation: TFunction<"translation", undefined>
): string {
	switch (status) {
		case AdminViewOrderStatus.UNPAID:
			return translation("Unpaid");
		case AdminViewOrderStatus.PAID:
			return translation("Paid");
		case AdminViewOrderStatus.IN_PROGRESS:
			return translation("In Progress");

		// case AdminViewOrderStatus.READY_TO_SHIP:
		// 	return translation("Ready to ship");
		case AdminViewOrderStatus.SHIPPED:
			return translation("Shipped");
		case AdminViewOrderStatus.DELIVERED:
			return translation("Delivered");
	}
}

export function getAdminStatus(
	status: OrderStatus,
	isOrderPaid: boolean
): AdminViewOrderStatus | undefined {
	switch (status) {
		case OrderStatus.INCOMPLETE:
			return undefined;
		case OrderStatus.PENDING:
			if (isOrderPaid) return AdminViewOrderStatus.PAID;
			else return AdminViewOrderStatus.UNPAID;
		case OrderStatus.IN_PROGRESS:
			return AdminViewOrderStatus.IN_PROGRESS;

		// case OrderStatus.READY_TO_SHIP:
		// 	return AdminViewOrderStatus.READY_TO_SHIP;

		case OrderStatus.SHIPPED:
			return AdminViewOrderStatus.SHIPPED;
		case OrderStatus.DELIVERED:
			return AdminViewOrderStatus.DELIVERED;
	}
}

export function getAdminStatusPermission(
	status: AdminViewOrderStatus
): Permission {
	switch (status) {
		case AdminViewOrderStatus.UNPAID:
			return Permission.ADMIN;
		case AdminViewOrderStatus.PAID:
			return Permission.ADMIN;
		case AdminViewOrderStatus.IN_PROGRESS:
			return Permission.ENGINEER;
		// case AdminViewOrderStatus.READY_TO_SHIP:
		// 	return Permission.ENGINEER;
		case AdminViewOrderStatus.SHIPPED:
			return Permission.SHIPPING;
		case AdminViewOrderStatus.DELIVERED:
			return Permission.ADMIN;
	}
}

export function getOrderStatusFromAdminStatus(
	status: AdminViewOrderStatus
): OrderStatus {
	switch (status) {
		case AdminViewOrderStatus.PAID:
			return OrderStatus.PENDING;
		case AdminViewOrderStatus.UNPAID:
			return OrderStatus.PENDING;
		case AdminViewOrderStatus.IN_PROGRESS:
			return OrderStatus.IN_PROGRESS;

		case AdminViewOrderStatus.SHIPPED:
			return OrderStatus.SHIPPED;
		// case AdminViewOrderStatus.READY_TO_SHIP:
		// 	return OrderStatus.READY_TO_SHIP;
		case AdminViewOrderStatus.DELIVERED:
			return OrderStatus.DELIVERED;
	}
}

export function getAdminStatusString(
	status: OrderStatus,
	orderPaid: boolean,
	translation: TFunction<"translation", undefined>
): string {
	switch (status) {
		case OrderStatus.INCOMPLETE:
			return translation("Incomplete");
		case OrderStatus.PENDING:
			return translation(orderPaid ? "Paid" : "Unpaid");
		case OrderStatus.IN_PROGRESS:
			return translation("InProgress");
		// case OrderStatus.READY_TO_SHIP:
		// 	return translation("ReadyToShip");
		case OrderStatus.SHIPPED:
			return translation("Shipped");
		case OrderStatus.DELIVERED:
			return translation("Delivered");
	}
}

export function getAdminStatusDescriptionText(
	status: OrderStatus,
	orderViewedByAdmin: boolean,

	translation: TFunction<"translation", undefined>
): string {
	switch (status) {
		case OrderStatus.INCOMPLETE:
			return translation("IncompleteOrderDescription");
		case OrderStatus.PENDING:
			if (orderViewedByAdmin)
				return translation("WaitingForArrivalDescription");
			else return translation("NewOrderDescription");
		case OrderStatus.IN_PROGRESS:
			return translation("InProgressOrderDescription");
		// case OrderStatus.READY_TO_SHIP:
		// 	return translation("ReadyToShipOrderDescription");
		case OrderStatus.SHIPPED:
			return translation("ShipOrderDescription");

		case OrderStatus.DELIVERED:
			return translation("ReceiveOrderDescription");
	}
}

export function getStatusColors(status: OrderStatus): {
	backgroundColor: string;
	borderColor: string;
	textColor: string;
} {
	switch (status) {
		case OrderStatus.INCOMPLETE:
			return {
				backgroundColor: SemanticColor.ERROR_100,
				borderColor: SemanticColor.ERROR_MEDIUM,
				textColor: SemanticColor.ERROR_300,
			};
		case OrderStatus.PENDING:
			return {
				backgroundColor: SemanticColor.WARNING_100,
				borderColor: SemanticColor.WARNING,
				textColor: SemanticColor.WARNING_300,
			};
		case OrderStatus.IN_PROGRESS:
			return {
				backgroundColor: SecondaryColor.SECONDARY_100,
				borderColor: SecondaryColor.SECONDARY_800,
				textColor: SecondaryColor.SECONDARY_800,
			};
		case OrderStatus.SHIPPED:
			return {
				backgroundColor: SemanticColor.SUCCESS_100,
				borderColor: SemanticColor.SUCCESS,
				textColor: SemanticColor.SUCCESS_300,
			};
		// case OrderStatus.READY_TO_SHIP:
		// 	return {
		// 		backgroundColor: SemanticColor.SUCCESS_100,
		// 		borderColor: SemanticColor.SUCCESS,
		// 		textColor: SemanticColor.SUCCESS_300,
		// 	};
		case OrderStatus.DELIVERED:
			return {
				backgroundColor: SemanticColor.SUCCESS_100,
				borderColor: SemanticColor.SUCCESS,
				textColor: SemanticColor.SUCCESS_300,
			};
	}
}

export function getAdminStatusColors(
	status: OrderStatus,
	orderViewedByAdmin: boolean,
	paid: boolean
): {
	backgroundColor: string;
	borderColor: string;
	textColor: string;
} {
	switch (status) {
		case OrderStatus.INCOMPLETE:
			//not in use
			return {
				backgroundColor: SemanticColor.ERROR_100,
				borderColor: SemanticColor.ERROR_MEDIUM,
				textColor: SemanticColor.ERROR_300,
			};
		case OrderStatus.PENDING:
			if (!paid)
				return {
					backgroundColor: SemanticColor.ERROR_100,
					borderColor: SemanticColor.ERROR_MEDIUM,
					textColor: SemanticColor.ERROR_300,
				};
			else
				return {
					backgroundColor: SemanticColor.SUCCESS_100,
					borderColor: SemanticColor.SUCCESS_300,
					textColor: SemanticColor.SUCCESS_300,
				};
		case OrderStatus.IN_PROGRESS:
			return {
				backgroundColor: AccentColor.ORANGE_100,
				borderColor: AccentColor.ORANGE_300,
				textColor: AccentColor.ORANGE_300,
			};

		case OrderStatus.SHIPPED:
			return {
				backgroundColor: SecondaryColor.SECONDARY_100,
				borderColor: SecondaryColor.SECONDARY_700,
				textColor: SecondaryColor.SECONDARY_700,
			};
		// case OrderStatus.READY_TO_SHIP:
		// 	return {
		// 		backgroundColor: SemanticColor.SUCCESS_300,
		// 		borderColor: SemanticColor.SUCCESS,
		// 		textColor: SemanticColor.SUCCESS_300,
		// 	};
		case OrderStatus.DELIVERED:
			return {
				backgroundColor: AccentColor.GREEN_100,
				borderColor: AccentColor.GREEN_300,
				textColor: AccentColor.GREEN_300,
			};
	}
}

export function getOrderSortString(
	sortColumn: OrderTableSortColumn,
	translation: TFunction<"translation", undefined>
): string {
	switch (sortColumn) {
		case OrderTableSortColumn.BARCODE_ID:
			return translation("OrderNumber");
		case OrderTableSortColumn.INSERTED:
			return translation("CreatedTime");
		case OrderTableSortColumn.SUBMITTED:
			return translation("SubmissionTime");
		case OrderTableSortColumn.REGISTERED:
			return translation("RegisteredTime");
	}
}

export function getOrderAvailableTAT(
	order: FrontendOrder
): Array<TurnaroundTime> {
	const result: Set<TurnaroundTime> = new Set();
	for (const batch of order.sampleBatches) {
		for (const sample of batch.sampleTestInfo) {
			result.add(sample.turnaroundTime);
		}
	}
	return Array.from(result);
}

export function getOrderTypeString(
	type: OrderType,
	translation: TFunction<"translation", undefined>
): string {
	switch (type) {
		case OrderType.INSTRUMENT:
			return translation("InstrumentOrder");
		case OrderType.SERVICE:
			return translation("ServiceOrder");
	}
}

export function getPaymentStatusString(
	status: PaymentStatus,
	translation: TFunction<"translation", undefined>
): string {
	switch (status) {
		case PaymentStatus.DOWNPAYMENT:
			return translation("Downpaid");
		case PaymentStatus.PAYINFULL:
			return translation("PayInFull");
		case PaymentStatus.PURCHASEORDER:
			return translation("PO#");
	}
}

export function getWarrantyDueDate(
	startDate: Date,
	period: number,
	unit: WarrantyUnit
): Date {
	const dueDate = new Date(startDate);
	if (unit === WarrantyUnit.DAY) {
		dueDate.setDate(dueDate.getDate() + period);
	} else if (unit === WarrantyUnit.MONTH) {
		dueDate.setMonth(dueDate.getMonth() + period);
	} else if (unit === WarrantyUnit.YEAR) {
		dueDate.setFullYear(dueDate.getFullYear() + period);
	}
	return dueDate;
}

export function getWarrantyTagTextColor(
	item: FrontendOrderItem,
	translation: TFunction<"translation", undefined>,
	delivered?: Date
): { text: string; color: string } {
	const today = new Date();
	const oneMonthFromNow = new Date();
	oneMonthFromNow.setMonth(oneMonthFromNow.getMonth() + 1);
	if (!delivered || !item.product.warranty.dueOn) {
		return {
			text: translation("Ineffective"),
			color: SemanticColor.INFO_MEDIUM,
		};
	} else if (
		item.product.warranty.dueOn > today &&
		item.product.warranty.dueOn <= oneMonthFromNow
	) {
		return { text: translation("Approaching"), color: SemanticColor.WARNING };
	} else if (item.product.warranty.dueOn < today) {
		return { text: translation("Expired"), color: SemanticColor.ERROR };
	} else {
		return { text: translation("Active"), color: SemanticColor.SUCCESS };
	}
}

//Fine the default item in wrranty card with active warranty and earliest due date
export function getDefaultWarrantyItem(warrantyItemList: FrontendOrderItem[]) {
	const now = new Date();

	const deliveredCandidates = warrantyItemList.filter(
		(item) => item.product.warranty.dueOn
	);

	if (deliveredCandidates.length > 0) {
		const active = deliveredCandidates.filter(
			(item) => (item.product.warranty.dueOn as Date) > now
		);

		const sorted = (active.length > 0 ? active : deliveredCandidates).sort(
			(a, b) =>
				a.product.warranty.dueOn!.getTime() -
				b.product.warranty.dueOn!.getTime()
		);

		return sorted[0];
	}
	return warrantyItemList[0];
}
